import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import { Box, Typography, IconButton, TextField } from "@mui/material";

import { useFootballFacilities } from "../../../../../../../Entity/api/queries/useFootballFacilities";
import { FootballFacility } from "../../../../../../../Entity/api/types/footballFacilities.types";
import { AddEntityLocationsFormData } from "../../../../../../types/editEntityProfile-type";
import { MinusButtonIcon, PlusButtonIcon } from "../../../../../../assets/icons/entity-icons";

type FacilityCount = {
  facilityId: number;
  count: number;
};

const FacilitiesTab = ({ locationIdx, branchIdx }: { locationIdx: number; branchIdx: number }) => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityProfileEdit" });
  const { control } = useFormContext<AddEntityLocationsFormData>();
  const { data: footballFacilities, isLoading: isFootballFacilitiesLoading } = useFootballFacilities();

  if (!footballFacilities || isFootballFacilitiesLoading) return null;

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t("facilities")}</Typography>
      <Controller
        name={`locations.${locationIdx}.branches.${branchIdx}.facilities`}
        control={control}
        render={({ field }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {footballFacilities.map((facilityGroup: FootballFacility) => (
              <Box key={facilityGroup.id} sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <Typography variant="subtitle2">{facilityGroup.name}</Typography>
                {facilityGroup.items.map((item) => {
                  const facilityCount = (field.value || []).find((f: FacilityCount) => f.facilityId === item.id) || {
                    facilityId: item.id,
                    count: 0,
                  };
                  return (
                    <FacilityField
                      key={item.id}
                      facilityId={item.id}
                      facilityName={item.name}
                      count={facilityCount.count}
                      onChange={(updatedFacility) => {
                        const currentFacilities = field.value || [];
                        const updatedFacilities = currentFacilities.map((f: FacilityCount) =>
                          f.facilityId === item.id ? updatedFacility : f
                        );
                        if (!currentFacilities.some((f: FacilityCount) => f.facilityId === item.id)) {
                          updatedFacilities.push(updatedFacility);
                        }
                        field.onChange(updatedFacilities);
                      }}
                    />
                  );
                })}
              </Box>
            ))}
          </Box>
        )}
      />
    </Box>
  );
};

export default FacilitiesTab;

const FacilityField = ({
  facilityId,
  facilityName,
  count,
  onChange,
}: {
  facilityId: number;
  facilityName: string;
  count: number;
  onChange: (facility: { facilityId: number; count: number }) => void;
}) => {
  const handleChange = (newCount: number) => {
    if (newCount >= 0 && newCount <= 99) {
      onChange({ facilityId, count: newCount });
    }
  };

  const handlePlus = () => {
    if (count < 99) {
      onChange({ facilityId, count: count + 1 });
    }
  };

  const handleMinus = () => {
    if (count > 0) {
      onChange({ facilityId, count: count - 1 });
    }
  };

  return (
    <Box sx={styles.facilityFieldContainer}>
      <Typography sx={styles.facilityName}>{facilityName}</Typography>
      <Box sx={styles.countContainer}>
        <IconButton onClick={handleMinus} size="small">
          <MinusButtonIcon />
        </IconButton>
        <TextField
          value={count}
          onChange={(e) => handleChange(Number(e.target.value))}
          size="small"
          sx={styles.countInput}
        />
        <IconButton onClick={handlePlus} size="small">
          <PlusButtonIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  title: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  facilityFieldContainer: {
    padding: "10px 16px",
    borderRadius: "8px",
    border: "1px solid var(--Gray-300, #D0D5DD)",
    background: "var(--Base-White, #FFF)",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  facilityName: {
    color: "var(--Gray-700, #344054)",
    textOverflow: "ellipsis",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    flex: 1,
  },
  countContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  countInput: {
    width: "48px",
    "& .MuiInputBase-input": {
      textAlign: "center",
      padding: "4px",
    },
  },
};
